import {
  Component,
  OnDestroy,
  OnChanges,
  Input,
  SimpleChanges,
  NgZone,
  PLATFORM_ID,
  Inject,
  OnInit,
} from "@angular/core";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { DashboardService } from "../dashboard.service";
import { ChartDataService } from "../../../helpers/chart-data.service";
import { am5ChartLicense } from "../../../app.constants";
import {
  colorMapping,
  leenbakkerColorMapping,
  praxisColorMapping,
} from "../bar-chart/bar-chart.model";
import { isPlatformBrowser } from "@angular/common";
@Component({
  selector: "app-line-chart",
  templateUrl: "./line-chart.component.html",
})
export class LineChartComponent
  implements OnInit, OnDestroy, OnChanges, OnInit
{
  @Input() startDate!: Date;
  @Input() endDate!: Date;
  @Input() categoryIds!: number[];
  @Input() apiType: string = "bookingPercent";
  @Input() companyId!: number;
  @Input() chartContainerId: string =
    "barChartDiv" + Math.random().toString(36).substring(2, 9); // Unique ID for chart container

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes in inputs and update the chart
    if (
      changes["categoryIds"] ||
      changes["companyId"] ||
      changes["apiType"] ||
      changes["startDate"]?.currentValue ||
      changes["endDate"]?.currentValue
    ) {
      this.loadChartData();
    }
  }

  ngOnInit(): void {
    const companyIdval = localStorage.getItem("companyId");
    this.companyId = companyIdval ? Number.parseInt(companyIdval, 10) : 0;
  }

  private root: am5.Root | undefined;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone,
    private dashboardService: DashboardService,
    private chartDataService: ChartDataService,
  ) {}

  // Fetch data from API and bind to chart
  loadChartData() {
    const params = {
      categoryIds: this.categoryIds,
      startDate: this.startDate,
      endDate: this.endDate,
      companyId: this.companyId,
    };
    if (!params.startDate || !params.endDate) {
      return;
    }

    const apiCall =
      this.apiType === "bookingPercent"
        ? this.dashboardService.getLeebakkerdashboardBookingsCountPer(params)
        : this.dashboardService.getDashboardBookings(params);

    apiCall.subscribe(
      (data: any[]) => {
        // Dispose of the existing chart to avoid multiple root errors
        if (this.root) {
          this.root.dispose();
        }

        if (!data || data.length === 0) {
          return;
        }

        // For companyId == 20 (Leen Bakker), handle differently
        // yLabel is always the same, so we treat it as a single series
        if (this.companyId === 20) {
          const transformedData: Record<string, any>[] = [];

          // Group by xValue and sum up yValues since yLabel is always the same
          data?.forEach((item) => {
            let existingXValue = transformedData.find(
              (d) => d["xValue"] === item.xValue,
            );

            if (!existingXValue) {
              existingXValue = { xValue: item.xValue };
              transformedData.push(existingXValue);
            }

            // Sum up yValues for the same xValue
            existingXValue[item.yLabel] =
              (existingXValue[item.yLabel] || 0) + item.yValue;
          });

          const processedData =
            this.chartDataService.convertXValueFromYearMonthToMonthName(
              transformedData,
            );

          this.createChart(processedData);
          return;
        }

        // For other companyIds, use the original logic (multiple series based on yLabel)
        const transformedData: Record<string, any>[] = [];
        if (data) {
          // Group by xValue and accumulate yValues for each yLabel
          data?.forEach((item) => {
            let existingXValue = transformedData.find(
              (d) => d["xValue"] === item.xValue,
            );

            if (!existingXValue) {
              existingXValue = { xValue: item.xValue }; // Create a new object for this xValue
              transformedData.push(existingXValue);
            }

            // Assign the yValue to the corresponding yLabel (series) in the object
            existingXValue[item.yLabel] = item.yValue;
          });

          // Sort the data's XValues correctly and display written labels instead of numbers.
          const processedData =
            this.chartDataService.convertXValueFromYearMonthToMonthName(
              transformedData,
            );
          this.createChart(processedData); // Pass transformed data to createChart
        }
      },
      (error) => {
        return;
      },
    );
  }

  createChart(data: any[]): void {
    this.browserOnly(() => {
      const element = document.getElementById(this.chartContainerId);
      if (!element) return;

      if (this.root) {
        this.root.dispose();
      }

      am5.addLicense(am5ChartLicense);

      this.root = am5.Root.new(this.chartContainerId);

      if (!this.root) {
        console.error("Root element is not defined");
        return;
      }

      this.root.setThemes([am5themes_Animated.new(this.root)]);
      this.root._logo?.dispose(); // Remove the logo to avoid clutter

      // const chart = this.root.container.children.push(
      //   am5xy.XYChart.new(this.root, {
      //     panX: false,
      //     panY: false,
      //     wheelX: "none", // Disable zooming on X-axis using the mouse wheel
      //     wheelY: "none", // Disable zooming on Y-axis using the mouse wheel
      //     paddingLeft: 0,
      //     layout: this.root.horizontalLayout,
      //   })
      // );
      // Root vertical container = chart at top, legend at bottom
      const mainContainer = this.root.container.children.push(
        am5.Container.new(this.root, {
          layout: this.root.verticalLayout, // <<< vertical layout
          width: am5.percent(100),
          height: am5.percent(100),
        }),
      );

      // Chart container
      const chart = mainContainer.children.push(
        am5xy.XYChart.new(this.root, {
          width: am5.percent(100),
          height: am5.percent(100),
          panX: false,
          panY: false,
          wheelX: "none",
          wheelY: "none",
          layout: this.root.horizontalLayout,
        }),
      );

      chart.set(
        "scrollbarX",
        am5.Scrollbar.new(this.root, {
          orientation: "horizontal",
        }),
      );

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(this.root, {
          categoryField: "xValue", // Use 'xValue' for the x-axis
          renderer: am5xy.AxisRendererX.new(this.root, {
            minorGridEnabled: true,
          }),
        }),
      );

      // Make X-axis labels more visible and readable
      xAxis.get("renderer").labels.template.setAll({
        fontSize: 18,
        rotation: -45,
        maxWidth: 100,
        textAlign: "center",
        centerX: am5.p100,
        centerY: am5.p50,
        oversizedBehavior: "wrap",
      });

      // Remove vertical grid lines by setting visibility to false
      xAxis.get("renderer").grid.template.setAll({
        visible: false,
      });

      xAxis.get("renderer").setAll({
        minGridDistance: 20,
      });

      // Extract unique yLabels (series names)
      const uniqueYLabels = [
        ...new Set(
          data.flatMap((item) =>
            Object.keys(item).filter((key) => key !== "xValue"),
          ),
        ),
      ];

      let highestY = 0;

      data.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (key !== "xValue") {
            const val = Number(row[key]) || 0;
            if (val > highestY) highestY = val;
          }
        });
      });

      // Set xAxis data based on the transformed data
      xAxis.data.setAll(data); // Directly set the transformed data

      let computedMax;
      let strictMinMaxValue;
      let extraConfig = {}; // will hold additional style differences

      if (this.companyId === 20) {
        // --- CASE 1: COMPANY 20 ---
        computedMax = 100;
        strictMinMaxValue = true;

        extraConfig = {
          // optional: smoother steps, cleaner UI
          numberFormat: "#'%'",
          maxPrecision: 0,
        };
      } else {
        // --- CASE 2: ANY OTHER COMPANY ---
        computedMax = highestY;
        strictMinMaxValue = false;

        extraConfig = {
          // force nicer grid, reduce auto-scaling jumps
          maxPrecision: 0,
          extraMax: 0.5, // tiny expansion margin
          calculateTotals: false,
        };
      }

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(this.root, {
          min: 0,
          max: computedMax,
          strictMinMax: strictMinMaxValue,
          renderer: am5xy.AxisRendererY.new(this.root, {
            strokeOpacity: 0.1,
            minGridDistance: this.companyId === 20 ? 10 : (this.companyId == 1 ? 20 : 25),
            // smaller grid for 20, wider for others
          }),
          ...extraConfig,
        }),
      );
     
      // Add a series for each unique yLabel (series name)
      uniqueYLabels.forEach((yLabel) => {
        this.makeLineSeries(yLabel, yLabel, chart, xAxis, yAxis, data);

        // Show yValueCount (sum of y values for this series)
        // const yValueCount = data.reduce((sum, item) => sum + (item[yLabel] || 0), 0);
        // Add a label to show yValueCount at the end of the line series

        // if (data.length > 0) {
        //   const lastItem = data[data.length - 1];
        //   chart.children.push(
        //     am5.Label.new(this.root!, {
        //       text: `Total Bookings ${yValueCount}`,
        //       x: am5.p100,
        //       y: am5.percent(10 + uniqueYLabels.indexOf(yLabel) * 15), // Offset each label vertically
        //       centerX: am5.p100,
        //       centerY: am5.p0,
        //       fill: am5.color(colorMapping[yLabel] || "#3357FF"),
        //       fontWeight: "bold",
        //       fontSize: 14,
        //       background: am5.RoundedRectangle.new(this.root!, {
        //       fill: am5.color(0xffffff),
        //       fillOpacity: 0.8,
        //       }),
        //     })
        //   );
        // }
      });

      // Optional legend
      if (this.companyId === 20) {
        const legend = mainContainer.children.push(
          am5.Legend.new(this.root, {
            nameField: "name",
            fillField: "fill",
            strokeField: "stroke",
            centerX: am5.percent(50),
            x: am5.percent(55),
            paddingTop: 20,
          }),
        );

        legend.data.setAll(chart.series.values);
      }

      chart.appear(1000, 100);
    });
  }

  makeLineSeries(
    name: string,
    fieldName: string,
    chart: any,
    xAxis: any,
    yAxis: any,
    data: any[],
  ): void {
    if (!this.root) {
      console.error("Root element is not defined");
      return;
    }

    let seriesColor;

    if (this.companyId === 4) {
      seriesColor = am5.color(praxisColorMapping[name]);
    } else if (this.companyId === 20) {
      seriesColor = am5.color(leenbakkerColorMapping[name]);
    } else {
      seriesColor = am5.color(colorMapping[name]);
    }

    // Remove any theme applications on root
    this.root.setThemes([]); // Clear themes for absolute control over styles

    // Create the LineSeries and ensure it references the correct yAxis and fieldName
    const series = chart.series.push(
      am5xy.LineSeries.new(this.root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: fieldName,
        categoryXField: "xValue",
        stroke: seriesColor, // Set the color directly on the series
      }),
    );

    series.strokes.template.setAll({
      strokeWidth: 2,
      stroke: seriesColor,
    });

    // Prepare data for this specific series
    const seriesData = data.map((item) => ({
      xValue: item.xValue,
      [fieldName]: item[fieldName] || 0,
    }));

    series.data.setAll(seriesData);

    // Set up the tooltip with explicit color properties for background and text
    const label =
      fieldName == "Praxis laminaat leggen"
        ? `bookings received`
        : `bookings completed`;
    const tooltip = am5.Tooltip.new(this.root!, {
      labelText:
        this.companyId == 4
          ? `{${fieldName}} ${label}`
          : `{valueYField}: {${fieldName}}`,
    });

    // Apply color settings to tooltip background and label directly
    tooltip.get("background")!.adapters.add("fill", () => seriesColor); // Set the background color
    tooltip.get("background")!.adapters.add("stroke", () => seriesColor); // Set the border color
    tooltip.label.adapters.add("fill", () => am5.color(0xffffff)); // Set the text color to white

    // Apply the tooltip to the series
    series.set("tooltip", tooltip);

    // Add a cursor to improve interaction
    chart.set("cursor", am5xy.XYCursor.new(this.root!, {}));

    // Disables horizontal and vertical scrollbar
    chart.set("scrollbarX", null);
    chart.set("scrollbarY", null);

    // Add circular markers to each data point, matching the line color
    series.bullets.push(() =>
      am5.Bullet.new(this.root!, {
        sprite: am5.Circle.new(this.root!, {
          radius: 4,
          fill: seriesColor,
          stroke: seriesColor,
        }),
      }),
    );

    series.appear();
  }

  ngOnDestroy() {
    if (this.root) {
      this.root.dispose(); // Properly dispose of the chart root to clean up resources
    }
  }

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }
}
